import { Either, Validation } from 'monet';

/**
 * @description Префикс имени шаблона.
 * @type {string}
 */
const PREFIX_NAME = 'ОБРАЗЕЦ';

/**
 * @description Шаблон
 * @param {string[]} allowedActions Допустимые действия.
 * @param id       {number}  Id
 * @param isSample {boolean} Образец ли
 * @param name     {string}  Имя
 * @param {string} removingId       Идентификатор для удаления.
 * @param type     {string}  Тип
 * @param typeName {string}  Имя типа
 * @constructor
 */
function Template(allowedActions, id, isSample, name, removingId, type, typeName) {
  this.id = id;
  this.isCanBeRemoved = allowedActions.includes('remove');
  this.isSample = isSample;
  this.name = name;
  this.removingId = removingId;
  this.type = type;
  this.typeName = typeName;

  /**
   * @description Получить имя с префиксом.
   * @returns {string}
   */
  this.getNameWithPrefix = () => (this.isSample ? `${PREFIX_NAME} ${this.name}` : this.name);
}

/**
 * @description Шлюз шаблонов документов.
 * @param {Axios} axios
 */
export default function DocumentTemplateGateway(axios) {
  /**
   * @description Добавить пациенту шаблон.
   * @param {string} patientId
   * @param {string} templateId
   * @param {string} templateType
   * @returns {Promise<Either>}
   */
  this.addPatientTemplate = (patientId, templateId, templateType) => axios
    .post(`/api/patient/${patientId}/template/${templateId}`, { template_type: templateType })
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Добавить пациенту шаблон по образцу.
   * @param {string} patientId
   * @param {string} sampleId
   * @param {string} templateType
   * @returns {Promise<Either>}
   */
  this.addPatientTemplateBySample = (patientId, sampleId, templateType) => axios
    .post(`/api/patient/${patientId}/sample/${sampleId}`, { template_type: templateType })
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Удалить документ у пациента.
   *
   * @param {string} patientId
   * @param {string} templateId
   * @param {string} type
   * @returns {*}
   */
  this.deletePatientTemplate = (patientId, templateId, type) => axios
    .delete(`/api/patient/${patientId}/template/${templateId}`, { data: { type } })
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Удалить шаблон.
   * @param {string} templateId Идентификатор шаблона.
   * @returns {Promise<Either>}
   */
  this.deleteTemplate = (templateId) => axios
    .delete(`/api/document-templates/${templateId}`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 404
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить список шаблонов для просмотра.
   * @param {string} search
   * @returns {Promise<Either>}
   */
  this.viewList = (search = '') => axios
    .get('/api/templates', { params: { search } })
    .then(
      (response) => {
        const templates = response.data.data.map((item) => new Template(
          item.allowed_actions,
          item.id,
          item.is_sample,
          item.name,
          item.removing_id,
          item.type,
          item.type_name,
        ));
        return Either.of(templates);
      },
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить список шаблонов по пациенту для просмотра.
   * @param {string} patientId
   * @param {string} search
   * @param {string} sort
   * @returns {Promise<Either>}
   */
  this.viewPatientList = (patientId, search = '', sort = '') => axios
    .get('/api/patient-templates', { params: { patient_id: patientId, search, sort } })
    .then(
      (response) => Either.of({
        list: response.data.data.list,
        patientUnderFifteen: response.data.data.patient_under_fifteen,
      }),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить сгенерированный документ.
   * @param {number} templateId Идентификатор.
   * @returns {Promise<Validation>}
   */
  this.generate = (templateId) => axios
    .get(`/api/templates/${templateId}`)
    .then(async (response) => Validation.Success(response.data));
}
